import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { useToast } from '../../hooks/useToastContext';
import {
  getPatients,
  getPayors,
  stediSubmitClaim837i,
  client,
} from '../../services/api';
import type {
  Stedi837iClaimInternalRequest,
  Stedi837iClaimResponseWithClaimId,
  PatientResponse,
  PayorResponse,
  StediAddress,
  Stedi837iReportInformation,
} from '../../services/api/types.gen';
import { stediPayerOptions } from './constants';
import SubscriberForm from './components/SubscriberForm';
import ProvidersForm from './components/ProvidersForm';
import ClaimInformationForm from './components/ClaimInformationForm';
import ServiceLinesForm from './components/ServiceLinesForm';
import type { Stedi837ITestingFormProps, AttachmentInfo } from './types';

// Create partial form data for initialization (patient_id and payor_id will be set on submit)
const defaultFormData: Omit<
  Stedi837iClaimInternalRequest,
  'patient_id' | 'payor_id'
> & { patient_id: string; payor_id: string } = {
  usageIndicator: 'T',
  tradingPartnerServiceId: '87726',
  tradingPartnerName: 'UnitedHealthcare',
  claimIdentifier: null,
  patient_id: '', // Will be set to UUID when submitting
  payor_id: '', // Will be set to UUID when submitting
  submitter: {
    organizationName: 'Test Facility',
    taxId: '123456789',
    contactInformation: {
      name: 'Test Facility',
      phoneNumber: '2225551234',
    },
  },
  receiver: {
    organizationName: 'UnitedHealthcare',
  },
  subscriber: {
    memberId: '98765',
    paymentResponsibilityLevelCode: 'P',
    firstName: 'JANE',
    lastName: 'DOE',
    gender: 'M',
    dateOfBirth: '19950729',
    groupNumber: '67890',
  },
  dependent: null,
  providers: [
    {
      providerType: 'BillingProvider',
      npi: '1999999976',
      employerId: '123456789',
      organizationName: 'Test Facility',
      address: {
        address1: '123 Mulberry Street',
        city: 'Seattle',
        state: 'WA',
        postalCode: '111135272',
      },
      contactInformation: {
        name: 'Test Facility',
        phoneNumber: '2065551234',
      },
    },
    {
      providerType: 'AttendingProvider',
      npi: '1999999976',
      firstName: 'Doctor',
      lastName: 'Provider',
      contactInformation: {
        name: 'Doctor Provider',
      },
    },
  ],
  billingPayToPlanName: null,
  billingPayToAddressName: null,
  operatingPhysician: null,
  otherOperatingPhysician: null,
  payorAddress: null,
  claimInformation: {
    claimFilingCode: 'CI',
    patientControlNumber: 'TEST01',
    claimChargeAmount: '500.00',
    placeOfServiceCode: '21',
    claimFrequencyCode: '1',
    planParticipationCode: 'A',
    benefitsAssignmentCertificationIndicator: 'Y',
    releaseInformationCode: 'Y',
    claimDateInformation: {
      statementBeginDate: '20241015',
      statementEndDate: '20241015',
    },
    principalDiagnosis: {
      qualifierCode: 'ABK',
      principalDiagnosisCode: 'M79.3',
    },
    admittingDiagnosis: {
      qualifierCode: 'ABJ',
      admittingDiagnosisCode: 'M79.3',
    },
    otherDiagnosisInformationList: [
      [{ qualifierCode: 'ABF', otherDiagnosisCode: 'E11.9' }],
      [{ qualifierCode: 'ABF', otherDiagnosisCode: 'I10' }],
    ],
    claimCodeInformation: {
      admissionTypeCode: '1',
      patientStatusCode: '01',
    },
    serviceLines: [
      {
        institutionalService: {
          serviceLineRevenueCode: '0110',
          procedureCode: '99213',
          procedureIdentifier: 'HC',
          lineItemChargeAmount: '500.00',
          measurementUnit: 'UN',
          serviceUnitCount: '1',
        },
      },
    ],
  },
};

const Stedi837ITestingForm: React.FC<Stedi837ITestingFormProps> = ({
  initialData,
  onSubmit,
}) => {
  const { success: showSuccess, error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] =
    useState<Stedi837iClaimResponseWithClaimId | null>(null);
  const [error, setError] = useState<string | null>(null);
  interface ValidationError {
    type?: string;
    loc?: string[];
    msg?: string;
    input?: unknown;
    field?: string;
    message?: string;
  }
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  );
  const [activeTab, setActiveTab] = useState('request');

  // Dropdown data state
  const [patients, setPatients] = useState<PatientResponse[]>([]);
  const [payors, setPayors] = useState<PayorResponse[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<string>('');
  const [selectedPayorId, setSelectedPayorId] = useState<string>('');

  // Form data state - using the same type as defaultFormData for consistency
  type FormDataType = typeof defaultFormData;
  const [formData, setFormData] = useState<FormDataType>({
    ...defaultFormData,
    ...initialData,
  } as FormDataType);

  // Attachment state management
  const [attachments, setAttachments] = useState<Map<string, AttachmentInfo>>(
    new Map()
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [patientsResponse, payorsResponse] = await Promise.all([
          getPatients({
            client,
          }),
          getPayors({
            client,
          }),
        ]);
        if (patientsResponse && patientsResponse.data) {
          setPatients(patientsResponse.data);
        }
        if (payorsResponse && payorsResponse.data) {
          setPayors(payorsResponse.data);
        }
      } catch (err) {
        console.error('Failed to fetch dropdown data:', err);
        showError('Failed to load patients and payors');
      }
    };

    fetchData();
  }, [showError]);

  // Generic form change handler
  const handleFormChange = (
    field: string,
    value: string | number | boolean | Record<string, unknown>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Subscriber change handler
  const handleSubscriberChange = (
    field: string,
    value: string | number | boolean | Record<string, unknown>
  ) => {
    setFormData(prev => ({
      ...prev,
      subscriber: {
        ...prev.subscriber,
        [field]: value,
      },
    }));
  };

  // Provider change handlers
  const handleProviderChange = (
    index: number,
    field: string,
    value: string | number | boolean | Record<string, unknown>
  ) => {
    setFormData(prev => {
      const newProviders = [...(prev.providers || [])];
      newProviders[index] = {
        ...newProviders[index],
        [field]: value,
      };
      return {
        ...prev,
        providers: newProviders,
      };
    });
  };

  const handleProviderAddressChange = (
    index: number,
    field: string,
    value: string
  ) => {
    setFormData(prev => {
      const newProviders = [...(prev.providers || [])];
      newProviders[index] = {
        ...newProviders[index],
        address: {
          address1: '',
          city: '',
          ...(newProviders[index]?.address || {}),
          [field]: value,
        } as StediAddress,
      };
      return {
        ...prev,
        providers: newProviders,
      };
    });
  };

  const handleAddProvider = () => {
    setFormData(prev => ({
      ...prev,
      providers: [
        ...(prev.providers || []),
        {
          providerType: 'BillingProvider',
          npi: '',
          organizationName: '',
          address: {
            address1: '',
            city: '',
            state: '',
            postalCode: '',
          },
          contactInformation: {
            name: '',
            phoneNumber: '',
          },
        },
      ],
    }));
  };

  const handleRemoveProvider = (index: number) => {
    setFormData(prev => ({
      ...prev,
      providers: (prev.providers || []).filter((_, i) => i !== index),
    }));
  };

  // Claim information change handlers
  const handleClaimInformationChange = (
    field: string,
    value: string | number | boolean | Record<string, unknown>
  ) => {
    setFormData(prev => ({
      ...prev,
      claimInformation: {
        ...prev.claimInformation,
        [field]: value,
      },
    }));
  };

  const handleClaimDateChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      claimInformation: {
        ...prev.claimInformation,
        claimDateInformation: {
          ...prev.claimInformation.claimDateInformation,
          [field]: value,
        },
      },
    }));
  };

  const handleDiagnosisChange = (
    field: string,
    value: Record<string, unknown>
  ) => {
    setFormData(prev => ({
      ...prev,
      claimInformation: {
        ...prev.claimInformation,
        [field]: value,
      },
    }));
  };

  const handleOtherDiagnosisChange = (
    index: number,
    field: 'otherDiagnosisCode' | 'qualifierCode',
    value: string
  ) => {
    setFormData(prev => {
      const updatedList = [
        ...(prev.claimInformation.otherDiagnosisInformationList || []),
      ];
      updatedList[index] = [
        {
          ...updatedList[index][0],
          [field]: value,
        },
      ];
      return {
        ...prev,
        claimInformation: {
          ...prev.claimInformation,
          otherDiagnosisInformationList: updatedList,
        },
      };
    });
  };

  const handleAddOtherDiagnosis = () => {
    setFormData(prev => ({
      ...prev,
      claimInformation: {
        ...prev.claimInformation,
        otherDiagnosisInformationList: [
          ...(prev.claimInformation.otherDiagnosisInformationList || []),
          [{ qualifierCode: 'ABF', otherDiagnosisCode: '' }],
        ],
      },
    }));
  };

  const handleRemoveOtherDiagnosis = (index: number) => {
    setFormData(prev => ({
      ...prev,
      claimInformation: {
        ...prev.claimInformation,
        otherDiagnosisInformationList: (
          prev.claimInformation.otherDiagnosisInformationList || []
        ).filter((_, i) => i !== index),
      },
    }));
  };

  const handleClaimCodeChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      claimInformation: {
        ...prev.claimInformation,
        claimCodeInformation: {
          ...prev.claimInformation.claimCodeInformation,
          [field]: value,
        },
      },
    }));
  };

  // Claim notes structure has changed - needs refactoring
  // claimNotes is now an object with goalRehabOrDischargePlans and diagnosisDescription arrays
  const handleClaimNotesChange = (
    _index: number,
    _field: string,
    _value: string
  ) => {
    // TODO: Update this to work with the new claimNotes structure
    console.warn(
      'handleClaimNotesChange needs to be updated for new claimNotes structure'
    );
  };

  const handleAddClaimNote = () => {
    setFormData(prev => ({
      ...prev,
      claimInformation: {
        ...prev.claimInformation,
        claimNotes: {
          ...prev.claimInformation.claimNotes,
          diagnosisDescription: [
            ...(prev.claimInformation.claimNotes?.diagnosisDescription || []),
            '',
          ],
        },
      },
    }));
  };

  const handleRemoveClaimNote = (index: number) => {
    setFormData(prev => ({
      ...prev,
      claimInformation: {
        ...prev.claimInformation,
        claimNotes: {
          ...prev.claimInformation.claimNotes,
          diagnosisDescription: (
            prev.claimInformation.claimNotes?.diagnosisDescription || []
          ).filter((_, i) => i !== index),
        },
      },
    }));
  };

  const handleServiceLineChange = (
    index: number,
    field: string,
    value: string | number | boolean | Record<string, unknown> | string[]
  ) => {
    setFormData(prev => {
      const newServiceLines = [...(prev.claimInformation.serviceLines || [])];
      newServiceLines[index] = {
        ...newServiceLines[index],
        [field]: value,
      };
      return {
        ...prev,
        claimInformation: {
          ...prev.claimInformation,
          serviceLines: newServiceLines,
        },
      };
    });
  };

  const handleInstitutionalServiceChange = (
    index: number,
    field: string,
    value: string | number | boolean | string[]
  ) => {
    setFormData(prev => {
      const newServiceLines = [...(prev.claimInformation.serviceLines || [])];
      newServiceLines[index] = {
        ...newServiceLines[index],
        institutionalService: {
          ...newServiceLines[index]?.institutionalService,
          [field]: value,
        },
      };
      return {
        ...prev,
        claimInformation: {
          ...prev.claimInformation,
          serviceLines: newServiceLines,
        },
      };
    });
  };

  const handleAddServiceLine = () => {
    setFormData(prev => ({
      ...prev,
      claimInformation: {
        ...prev.claimInformation,
        serviceLines: [
          ...(prev.claimInformation.serviceLines || []),
          {
            institutionalService: {
              serviceLineRevenueCode: '',
              procedureCode: '',
              procedureIdentifier: 'HC',
              lineItemChargeAmount: '',
              measurementUnit: 'UN',
              serviceUnitCount: '1',
            },
          },
        ],
      },
    }));
  };

  const handleRemoveServiceLine = (index: number) => {
    setFormData(prev => ({
      ...prev,
      claimInformation: {
        ...prev.claimInformation,
        serviceLines: (prev.claimInformation.serviceLines || []).filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  // Attachment handlers
  const handleAttachmentUpload = (
    attachment: AttachmentInfo,
    serviceLineNumber?: number
  ) => {
    const updatedAttachment = {
      ...attachment,
      serviceLineNumber: serviceLineNumber,
    };
    setAttachments(prev => new Map(prev).set(attachment.id, updatedAttachment));
  };

  const handleAttachmentRemove = (attachmentId: string) => {
    setAttachments(prev => {
      const newMap = new Map(prev);
      newMap.delete(attachmentId);
      return newMap;
    });
  };

  // Build the complete request payload with attachments
  const buildRequestPayload = (): Stedi837iClaimInternalRequest => {
    const requestData: Stedi837iClaimInternalRequest = {
      ...formData,
      patient_id: selectedPatientId || '',
      payor_id: selectedPayorId || '',
    };

    // Include attachment control numbers if we have attachments
    const allAttachments = Array.from(attachments.values());
    if (allAttachments.length > 0) {
      // Separate claim-level and service-line attachments
      const claimLevelAttachments = allAttachments.filter(
        a => a.serviceLineNumber === undefined
      );
      const serviceLineAttachments = allAttachments.filter(
        a => a.serviceLineNumber !== undefined
      );

      // Build reportInformations array for claim-level attachments
      if (claimLevelAttachments.length > 0) {
        const reportInformations: Stedi837iReportInformation[] =
          claimLevelAttachments.map(attachment => ({
            attachmentControlNumber: attachment.attachmentControlNumber,
            attachmentReportTypeCode: attachment.reportTypeCode || 'OZ', // OZ = Support Data for Claim
            attachmentTransmissionCode: 'EL', // EL = Electronically Only (for Stedi API)
          }));

        // Add to request data
        requestData.claimInformation = {
          ...requestData.claimInformation,
          claimSupplementalInformation: {
            ...requestData.claimInformation.claimSupplementalInformation,
            reportInformations,
          },
        };
      }

      // Handle service-line attachments
      if (serviceLineAttachments.length > 0) {
        // Update each service line with its attachments
        const updatedServiceLines =
          requestData.claimInformation.serviceLines.map(
            (serviceLine, index) => {
              // Find attachments for this service line (1-based indexing)
              const lineAttachments = serviceLineAttachments.filter(
                a => a.serviceLineNumber === index + 1
              );

              if (lineAttachments.length > 0) {
                // Convert attachments to the proper format - using correct Stedi field names
                const supplementalInfos = lineAttachments.map(attachment => ({
                  attachmentControlNumber: attachment.attachmentControlNumber,
                  attachmentReportTypeCode: attachment.reportTypeCode || 'OZ',
                  attachmentTransmissionCode: 'EL',
                  attachmentDescription: attachment.description,
                }));

                return {
                  ...serviceLine,
                  serviceLineSupplementalInformations: supplementalInfos,
                };
              }

              return serviceLine;
            }
          );

        requestData.claimInformation.serviceLines = updatedServiceLines;
      }
    }

    return requestData;
  };

  const handleSubmit = async () => {
    if (!selectedPatientId || !selectedPayorId) {
      showError('Please select both a patient and payor');
      return;
    }

    setIsLoading(true);
    setError(null);
    setValidationErrors([]);

    try {
      const requestData = buildRequestPayload();

      const result = await stediSubmitClaim837i({
        body: requestData,
        client,
      });
      if (result && result.data) {
        setResponse(result.data);
      }
      showSuccess('Claim submitted successfully!');
      setActiveTab('response'); // Only switch tabs on successful submission

      if (onSubmit) {
        await onSubmit(requestData);
      }
    } catch (err) {
      const error = err as Error & {
        response?: { data?: unknown };
        data?: unknown;
      };
      console.log('Full error object:', error);
      console.log('Error response:', error.response);
      console.log('Error response data:', error.response?.data);
      console.log('Error data:', error.data);
      console.log('Error body:', (err as { body?: unknown }).body);

      // Try multiple possible error data locations
      const errorData =
        error.response?.data ||
        error.data ||
        (err as { body?: unknown }).body ||
        err;
      const errorDetail = errorData as {
        detail?: ValidationError[] | string;
        message?: string;
      };

      console.log('Processed error data:', errorData);

      // Handle validation errors specifically - check multiple possible structures
      if (errorDetail?.detail && Array.isArray(errorDetail.detail)) {
        console.log(
          'Found validation errors in detail array:',
          errorDetail.detail
        );
        setValidationErrors(errorDetail.detail || []);
        setError('Validation errors occurred. Please check the details below.');
        showError(
          `${errorDetail.detail?.length || 0} validation error(s) occurred`
        );
      } else if (Array.isArray(errorData as unknown[])) {
        console.log('Error data is array:', errorData);
        setValidationErrors(errorData as ValidationError[]);
        setError('Validation errors occurred. Please check the details below.');
        showError(
          `${(errorData as ValidationError[]).length} validation error(s) occurred`
        );
      } else if (
        errorDetail?.detail &&
        typeof errorDetail.detail === 'string' &&
        errorDetail.detail.includes('Stedi API error:')
      ) {
        // Handle Stedi API errors
        console.log('Found Stedi API error:', errorDetail.detail);
        const stediError = {
          type: 'stedi_api_error',
          loc: ['stedi_api'],
          msg: errorDetail.detail,
          input: null,
        };
        setValidationErrors([stediError]);
        setError('Stedi API error occurred. Please check the details below.');
        showError('Stedi API validation error occurred');
      } else {
        const errorMessage =
          errorDetail?.message ||
          (err as Error).message ||
          'Failed to submit claim';
        console.log('Setting general error:', errorMessage);
        setError(errorMessage);
        setValidationErrors([]);
        showError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='container mx-auto p-6'>
      <Card>
        <CardHeader>
          <CardTitle>Stedi 837I Testing Tool</CardTitle>
          <CardDescription>
            Test 837I claim submissions to Stedi with various configurations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className='space-y-4'
          >
            <TabsList>
              <TabsTrigger value='request'>Request</TabsTrigger>
              <TabsTrigger value='response'>Response</TabsTrigger>
            </TabsList>

            <TabsContent value='request' className='space-y-6'>
              {/* Patient and Payor Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>Selection</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                    <div>
                      <label className='block text-sm font-medium mb-1'>
                        Patient <span className='text-red-500'>*</span>
                      </label>
                      <Select
                        value={selectedPatientId}
                        onValueChange={setSelectedPatientId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select patient' />
                        </SelectTrigger>
                        <SelectContent>
                          {patients.map(patient => (
                            <SelectItem key={patient.id} value={patient.id}>
                              {patient.first_name} {patient.last_name} -{' '}
                              {patient.date_of_birth}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className='block text-sm font-medium mb-1'>
                        Payor <span className='text-red-500'>*</span>
                      </label>
                      <Select
                        value={selectedPayorId}
                        onValueChange={setSelectedPayorId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select payor' />
                        </SelectTrigger>
                        <SelectContent>
                          {payors.map(payor => (
                            <SelectItem key={payor.id} value={payor.id}>
                              {payor.payor_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className='block text-sm font-medium mb-1'>
                        Trading Partner
                      </label>
                      <Select
                        value={formData.tradingPartnerServiceId}
                        onValueChange={value => {
                          const selectedPayer = stediPayerOptions.find(
                            p => p.value === value
                          );
                          handleFormChange('tradingPartnerServiceId', value);
                          handleFormChange(
                            'tradingPartnerName',
                            selectedPayer?.label || ''
                          );
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select trading partner' />
                        </SelectTrigger>
                        <SelectContent>
                          {stediPayerOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Subscriber Information */}
              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>
                    Subscriber Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SubscriberForm
                    subscriber={formData.subscriber}
                    onChange={handleSubscriberChange}
                  />
                </CardContent>
              </Card>

              {/* Providers */}
              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>Providers</CardTitle>
                  <CardDescription>
                    Manage billing, attending, referring, and rendering
                    providers
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ProvidersForm
                    providers={formData.providers || []}
                    onProviderChange={handleProviderChange}
                    onProviderAddressChange={handleProviderAddressChange}
                    onAddProvider={handleAddProvider}
                    onRemoveProvider={handleRemoveProvider}
                  />
                </CardContent>
              </Card>

              {/* Claim Information */}
              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>Claim Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <ClaimInformationForm
                    claimInformation={formData.claimInformation}
                    onChange={handleClaimInformationChange}
                    onClaimDateChange={handleClaimDateChange}
                    onDiagnosisChange={handleDiagnosisChange}
                    onOtherDiagnosisChange={handleOtherDiagnosisChange}
                    onAddOtherDiagnosis={handleAddOtherDiagnosis}
                    onRemoveOtherDiagnosis={handleRemoveOtherDiagnosis}
                    onClaimCodeChange={handleClaimCodeChange}
                    _onClaimNotesChange={handleClaimNotesChange}
                    onAddClaimNote={handleAddClaimNote}
                    _onRemoveClaimNote={handleRemoveClaimNote}
                    attachments={attachments}
                    onAttachmentUpload={handleAttachmentUpload}
                    onAttachmentRemove={handleAttachmentRemove}
                    selectedPatientId={selectedPatientId}
                  />
                </CardContent>
              </Card>

              {/* Service Lines */}
              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>Service Lines</CardTitle>
                  <CardDescription>
                    Add individual service line items for the claim
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ServiceLinesForm
                    serviceLines={formData.claimInformation.serviceLines || []}
                    onServiceLineChange={handleServiceLineChange}
                    onInstitutionalServiceChange={
                      handleInstitutionalServiceChange
                    }
                    onAddServiceLine={handleAddServiceLine}
                    onRemoveServiceLine={handleRemoveServiceLine}
                    attachments={attachments}
                    onAttachmentUpload={handleAttachmentUpload}
                    onAttachmentRemove={handleAttachmentRemove}
                    selectedPatientId={selectedPatientId}
                  />
                </CardContent>
              </Card>

              {/* Submit Button */}
              <div className='flex justify-end'>
                <Button
                  onClick={handleSubmit}
                  disabled={isLoading || !selectedPatientId || !selectedPayorId}
                  size='lg'
                >
                  {isLoading ? 'Submitting...' : 'Submit Claim'}
                </Button>
              </div>

              {/* API Validation Errors */}
              {validationErrors.length > 0 && (
                <Card className='border-red-200 bg-red-50'>
                  <CardHeader>
                    <CardTitle className='text-red-800'>
                      API Validation Errors
                    </CardTitle>
                    <CardDescription className='text-red-700'>
                      Please fix the following validation errors before
                      resubmitting:
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className='space-y-3'>
                      {validationErrors.map((error, index) => (
                        <div
                          key={index}
                          className='p-3 bg-red-100 border border-red-200 rounded'
                        >
                          <div className='font-medium text-red-800'>
                            {error.type === 'stedi_api_error'
                              ? 'Stedi API Error'
                              : `Field: ${error.loc?.slice(2).join(' → ') || 'Unknown'}`}
                          </div>
                          <div className='text-red-700 mt-1'>{error.msg}</div>
                          {error.input !== undefined &&
                            error.input !== null && (
                              <details className='mt-2'>
                                <summary className='text-sm text-red-600 cursor-pointer'>
                                  Show current value
                                </summary>
                                <pre className='text-xs bg-red-50 p-2 mt-1 rounded border'>
                                  {JSON.stringify(error.input, null, 2)}
                                </pre>
                              </details>
                            )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Real-time Request Payload */}
              <Card className='mt-6'>
                <CardHeader>
                  <CardTitle className='text-lg'>
                    Generated Request Payload
                  </CardTitle>
                  <CardDescription>
                    Live preview of the request that will be sent to the API
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <pre className='bg-gray-50 p-4 rounded text-sm overflow-auto max-h-96'>
                    {JSON.stringify(
                      selectedPatientId && selectedPayorId
                        ? buildRequestPayload()
                        : {
                            ...formData,
                            patient_id: selectedPatientId || '<Not Selected>',
                            payor_id: selectedPayorId || '<Not Selected>',
                          },
                      null,
                      2
                    )}
                  </pre>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value='response' className='space-y-6'>
              {error && (
                <Card className='border-red-200'>
                  <CardHeader>
                    <CardTitle className='text-red-700'>
                      Error Response
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className='bg-red-50 p-4 rounded text-sm overflow-auto text-red-800'>
                      {error}
                    </pre>
                  </CardContent>
                </Card>
              )}

              {response && (
                <Card className='border-green-200'>
                  <CardHeader>
                    <CardTitle className='text-green-700'>
                      Success Response
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className='bg-green-50 p-4 rounded text-sm overflow-auto text-green-800'>
                      {JSON.stringify(response, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              )}

              {!response && !error && (
                <Card>
                  <CardContent className='text-center py-8 text-gray-500'>
                    No response yet. Submit a claim to see the result.
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Stedi837ITestingForm;
